import { defineAsyncComponent, type Component } from "vue";
import { HomepageBlockKind } from "@/services/homepage";
import type { IconName } from "@/services/icons";
import { ORG_SCOPE_POSSESSIVE, PRIVATE_SCOPE } from "@/copy/scope";

export type BlockGroup = "Act on today" | "Improve" | "Explore" | "Product";

export interface BlockDefinition {
  label: string;
  description: string;
  icon: IconName;
  group: BlockGroup;
  component: Component;
  contentClass?: string;
  brand?: boolean;
  managerOnly?: boolean;
  /** Route name behind the header's "View details" link. */
  detailRoute?: string;
  minW: number;
  minH: number;
  defaultW: number;
  defaultH: number;
}

// Must stay a subset of the backend registry in backend/homepage/blocks.py.
export const BLOCK_REGISTRY: Record<HomepageBlockKind, BlockDefinition> = {
  setup_progress: {
    label: "Get more from Fyr",
    description: "Your user level, setup progress and the next things to try.",
    icon: "rocket-launch",
    group: "Improve",
    component: defineAsyncComponent(
      () => import("@/views/Home/blocks/SetupProgressBlock.vue"),
    ),
    minW: 4,
    minH: 4,
    defaultW: 12,
    defaultH: 4,
    detailRoute: "setup-progress",
  },
  pinned_kpis: {
    label: "Pinned KPIs",
    description: `Metrics pinned ${ORG_SCOPE_POSSESSIVE}, with their target status.`,
    icon: "thumbtack",
    group: "Act on today",
    component: defineAsyncComponent(
      () => import("@/views/Home/blocks/PinnedKpisBlock.vue"),
    ),
    contentClass: "p-0",
    minW: 3,
    minH: 2,
    defaultW: 12,
    defaultH: 3,
    detailRoute: "goals",
  },
  goals: {
    label: "Pinned Goals",
    description: `Progress and pacing for the goals pinned ${ORG_SCOPE_POSSESSIVE}.`,
    icon: "bullseye-arrow",
    group: "Act on today",
    component: defineAsyncComponent(
      () => import("@/views/Home/blocks/GoalsBlock.vue"),
    ),
    minW: 3,
    minH: 3,
    defaultW: 4,
    defaultH: 5,
    detailRoute: "goals",
  },
  feed: {
    label: "Feed",
    description: "Latest activity, alerts, and comments across your accounts.",
    icon: "newspaper-lucide",
    group: "Product",
    component: defineAsyncComponent(
      () => import("@/views/Home/blocks/FeedBlock.vue"),
    ),
    contentClass: "py-1 pr-4 pl-5",
    minW: 4,
    minH: 4,
    defaultW: 8,
    defaultH: 5,
  },
  whats_new: {
    label: "What's new in Fyr",
    description: "Product updates and new features in Fyr.",
    icon: "sparkles",
    group: "Product",
    component: defineAsyncComponent(
      () => import("@/views/Home/blocks/WhatsNewBlock.vue"),
    ),
    brand: true,
    minW: 3,
    minH: 3,
    defaultW: 4,
    defaultH: 5,
  },
  recommendations: {
    label: "Recommendations feed",
    description: "Suggested actions to improve performance.",
    icon: "lightbulb",
    group: "Improve",
    component: defineAsyncComponent(
      () => import("@/views/Home/blocks/RecommendationsBlock.vue"),
    ),
    minW: 3,
    minH: 3,
    defaultW: 4,
    defaultH: 5,
    detailRoute: "recommendations",
  },
  site_score: {
    label: "Site Score trend",
    description: "How your website health score has moved over time.",
    icon: "browser-light",
    group: "Explore",
    component: defineAsyncComponent(
      () => import("@/views/Home/blocks/SiteScoreBlock.vue"),
    ),
    minW: 3,
    minH: 3,
    defaultW: 4,
    defaultH: 3,
    detailRoute: "site-score",
  },
  my_tasks: {
    label: "My Tasks",
    description: "Recommendations assigned to you and their status.",
    icon: "circle-check",
    group: "Act on today",
    component: defineAsyncComponent(
      () => import("@/views/Home/blocks/MyTasksBlock.vue"),
    ),
    contentClass: "p-0",
    minW: 3,
    minH: 3,
    defaultW: 4,
    defaultH: 5,
  },
  favorite_dashboards: {
    label: "Favorite dashboards",
    description: `Quick links to the dashboards you favorited. ${PRIVATE_SCOPE}.`,
    icon: "table-cells",
    group: "Explore",
    component: defineAsyncComponent(
      () => import("@/views/Home/blocks/FavoriteDashboardsBlock.vue"),
    ),
    minW: 3,
    minH: 3,
    defaultW: 4,
    defaultH: 5,
    detailRoute: "dashboard-overview",
  },
  ai_credits: {
    label: "AI credit usage",
    description: "AI credits used this billing period.",
    icon: "coins-lucide",
    group: "Product",
    component: defineAsyncComponent(
      () => import("@/views/Home/blocks/AiCreditsBlock.vue"),
    ),
    managerOnly: true,
    minW: 3,
    minH: 3,
    defaultW: 4,
    defaultH: 3,
    detailRoute: "general",
  },
};

export function isKnownBlockKind(kind: string): kind is HomepageBlockKind {
  return kind in BLOCK_REGISTRY;
}
